import React, { useRef } from 'react';
import type { AnimatedValue } from 'react-spring';
import { animated, useSpring } from 'react-spring';
import type { BoxProps } from '../Box';
import { backdropStyles, StyledRoot } from './Backdrop.styles';
import { forwardRef } from '@shared/utils/forwardRef';

export interface BackdropProps
  extends Readonly<{
      isOpen: boolean;
      onFrame?: (styles: { opacity: number }) => void;
      delay?: number;
      duration?: number;
      /**
       * When mounted already open, start fully dimmed instead of fading up from transparent.
       * @default false
       */
      disableInitialTransition?: boolean;
    }>,
    BoxProps {}

export const Backdrop = forwardRef<'div', BackdropProps>((props, ref) => {
  const { delay, duration, isOpen, onFrame, disableInitialTransition, ...restProps } = props;
  // Captured at mount — see Modal's skipIntroSpring.
  const skipIntroSpring = useRef(!!disableInitialTransition && isOpen).current;
  const backdropSprings = useSpring({
    from: {
      opacity: skipIntroSpring ? 1 : 0,
    },
    to: {
      opacity: isOpen ? 1 : 0,
    },
    onFrame,
    delay,
    config: {
      duration,
    },
  }) as AnimatedValue<{ opacity: number }>;

  return (
    <StyledRoot
      data-testid="backdrop"
      as={animated.div}
      ref={ref}
      style={{ opacity: backdropSprings.opacity.interpolate(x => x) }}
      {...restProps}
      __css={backdropStyles}
    />
  );
});

Backdrop.defaultProps = {
  duration: 120,
};
Backdrop.displayName = 'Backdrop';
